import { createContext, useContext, useMemo } from 'react';
import { SettingsContext } from '@/providers/SettingsProvider';
import { ThemeContext } from '@/providers/ThemeProvider';
import type { ISettingsContext } from '@/providers/SettingsProvider';
import type { IThemeContext } from '@/providers/ThemeProvider';
import type { Settings } from '@/types/settings';
import type { ReactNode } from 'react';
interface IClassContext {
  settingsContext?: ISettingsContext;
  themeContext?: IThemeContext;
}
interface ClassProviderProps {
  children: ReactNode;
  settings?: Settings;
}
export const ClassContext = createContext<IClassContext>({});
ClassContext.displayName = 'ClassContext';

/**
 * A provider for global context for React classes.
 *
 * This exists to pull together the context provided by, for example, the theme
 * and settings providers. React classes can only make use of a single context.
 * This could go if everything was refactored to use functional components
 * (which would be quite a large task at this stage)!
 */
export const ClassProvider: React.FC<ClassProviderProps> = ({
  children
}) => {
  const settingsContext = useContext(SettingsContext);
  const themeContext = useContext(ThemeContext);
  const ctx = useMemo((): IClassContext => ({
    settingsContext,
    themeContext
  }), [settingsContext, themeContext]);
  return <ClassContext.Provider value={ctx} data-sentry-element="ClassContext.Provider" data-sentry-component="ClassProvider" data-sentry-source-file="index.tsx">{children}</ClassContext.Provider>;
};